'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import { LogOut, User } from 'lucide-react';

export default function LearnerTopbar() {
  const router = useRouter();
  const supabase = createClient();
  const [profile, setProfile] = useState<{ full_name: string; student_id: string | null; role: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setLoading(false); return; }
      const { data } = await supabase
        .from('user_profiles')
        .select('full_name, student_id, role')
        .eq('id', session.user.id)
        .single();
      if (data) setProfile(data);
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    router.push('/login');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 h-16 bg-card border-b border-border flex items-center px-6 lg:px-10">
      <div className="flex items-center gap-2 flex-1">
        <Link href="/" className="flex items-center gap-2 group">
          <AppLogo size={32} />
          <span className="font-extrabold text-lg tracking-tight text-foreground group-hover:text-primary transition-colors duration-150">
            iBrain Academy
          </span>
        </Link>
      </div>
      <nav className="hidden md:flex items-center gap-1">
        <Link
          href="/"
          className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
        >
          Subjects
        </Link>
        {!loading && profile && (
          <Link
            href="/profile"
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
          >
            My Profile
          </Link>
        )}
      </nav>
      <div className="flex items-center gap-3 ml-4">
        {!loading && profile ? (
          <>
            <Link
              href="/profile"
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg hover:bg-muted/80 transition-colors duration-150"
            >
              <User className="w-3.5 h-3.5 text-muted-foreground" />
              <div className="text-xs">
                <p className="font-semibold text-foreground leading-none">{profile.full_name}</p>
                {profile.student_id && (
                  <p className="text-muted-foreground mt-0.5">{profile.student_id}</p>
                )}
              </div>
            </Link>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </>
        ) : !loading ? (
          <Link
            href="/login"
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-150"
          >
            Sign in
          </Link>
        ) : null}
      </div>
    </header>
  );
}